import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import NavRail from "../components/NavRail";
import api, {
  closePosition,
  closePositionsBatch,
  getRiskState,
  getWalletBalance,
} from "../services/api";
import PositionDetailModal from "../components/PositionDetailModal";
import TokenDrillPanel from "../components/TokenDrillPanel";
import RuleDrillPanel from "../components/RuleDrillPanel";
import DateTimePicker from "../components/DateTimePicker";
import OutcomesBar from "../components/OutcomesBar";
import CopyBtn from "../components/CopyBtn";
import ModeBadge, { runtimeModeView } from "../components/ModeBadge";
import usePersistedState from "../hooks/usePersistedState";

/* ============================================================
   POSITIONS — Backoffice bot memecoin Solana (V1)
   Full-screen self-contained · dark terminal.
   Look = mockup design-docs/mockups/Positions.jsx
   Comportement = ancien components/Positions.js (100% préservé).
   ============================================================ */

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700;800&display=swap');
:root{
  --bg:#0a0b0e;--surface:#14161c;--surface2:#191c23;--border:#1f2329;
  --text:#e6e8ec;--text2:#8a8f98;
  --green:#00e08a;--red:#ff3b5c;--amber:#ffb020;--cyan:#22d3ee;
}
.ck,.ck *{font-family:'JetBrains Mono',ui-monospace,monospace;box-sizing:border-box;}
.ck{background:var(--bg);color:var(--text);height:100vh;display:flex;
  overflow:hidden;font-size:13px;line-height:1.35;-webkit-font-smoothing:antialiased;}

.sb{width:64px;flex:0 0 64px;border-right:1px solid var(--border);background:#0c0d11;
  display:flex;flex-direction:column;align-items:center;padding:14px 0;gap:6px;}
.sb-logo{width:34px;height:34px;border-radius:8px;background:var(--red);color:#fff;
  display:flex;align-items:center;justify-content:center;font-weight:800;font-size:16px;
  margin-bottom:14px;box-shadow:0 0 14px rgba(255,59,92,.45);}
.sb-item{width:40px;height:40px;border-radius:8px;display:flex;align-items:center;
  justify-content:center;color:var(--text2);cursor:pointer;position:relative;
  transition:all .12s;font-size:17px;border:none;background:transparent;}
.sb-item:hover{background:var(--surface);color:var(--text);}
.sb-item.on{background:var(--surface);color:var(--cyan);box-shadow:inset 2px 0 0 var(--cyan);}
.sb-item .tip{position:absolute;left:52px;top:50%;transform:translateY(-50%);background:#000;
  border:1px solid var(--border);padding:4px 9px;border-radius:6px;font-size:11px;
  white-space:nowrap;opacity:0;pointer-events:none;transition:opacity .12s;z-index:50;color:var(--text);}
.sb-item:hover .tip{opacity:1;}
.sb-spacer{flex:1;}

.main{flex:1;display:flex;flex-direction:column;overflow:hidden;}
.top{display:flex;align-items:center;gap:18px;padding:11px 18px;border-bottom:1px solid var(--border);
  background:#0c0d11;flex:0 0 auto;}
.mode{display:flex;align-items:center;gap:9px;padding:9px 16px;border-radius:9px;font-weight:800;
  font-size:14px;letter-spacing:.04em;}
.mode.live{background:var(--red);color:#fff;box-shadow:0 0 0 1px rgba(255,59,92,.6),0 0 22px rgba(255,59,92,.35);}
.mode.paper{background:rgba(0,224,138,.13);color:var(--green);border:1px solid rgba(0,224,138,.4);}
.dot{width:9px;height:9px;border-radius:50%;background:#fff;animation:pulse 1.3s infinite;}
@keyframes pulse{0%{box-shadow:0 0 0 0 rgba(255,255,255,.7);}70%{box-shadow:0 0 0 7px rgba(255,255,255,0);}100%{box-shadow:0 0 0 0 rgba(255,255,255,0);}}
.mode.paper .dot{background:var(--green);animation:pulseg 2.4s infinite;}
@keyframes pulseg{0%{box-shadow:0 0 0 0 rgba(0,224,138,.5);}70%{box-shadow:0 0 0 6px rgba(0,224,138,0);}100%{box-shadow:0 0 0 0 rgba(0,224,138,0);}}
.wallet{display:flex;flex-direction:column;gap:1px;}
.wallet .lbl{font-size:10px;color:var(--text2);letter-spacing:.12em;text-transform:uppercase;}
.wallet .val{font-size:22px;font-weight:700;}
.delta{display:flex;align-items:center;gap:5px;font-size:13px;font-weight:600;}
.delta.neg{color:var(--red);}.delta.pos{color:var(--green);}
.top-spacer{flex:1;}
.clock{font-size:11px;color:var(--text2);text-align:right;}
.clock b{color:var(--text);font-weight:600;}
.refresh{display:flex;align-items:center;gap:7px;background:var(--surface2);color:var(--text);
  border:1px solid var(--border);border-radius:9px;padding:10px 15px;font-weight:700;font-size:12px;
  cursor:pointer;font-family:inherit;transition:filter .12s;}
.refresh:hover{filter:brightness(1.2);}
.refresh:disabled{opacity:.5;cursor:not-allowed;}

.page{flex:1;overflow:auto;padding:16px;}
.h1{font-size:13px;letter-spacing:.16em;text-transform:uppercase;color:var(--text2);
  font-weight:600;margin-bottom:13px;display:flex;align-items:center;gap:10px;}
.h1 .badge{font-size:11px;background:var(--surface2);border:1px solid var(--border);
  padding:2px 8px;border-radius:20px;color:var(--cyan);letter-spacing:.05em;}

/* summary strip */
.strip{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin-bottom:16px;}
@media(max-width:820px){.strip{grid-template-columns:repeat(2,1fr);}}
.kpi{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:11px 13px;}
.kpi .k{font-size:10px;color:var(--text2);letter-spacing:.08em;text-transform:uppercase;}
.kpi .v{font-size:19px;font-weight:700;margin-top:4px;}
.kpi .v.pos{color:var(--green);}.kpi .v.neg{color:var(--red);}

/* filters */
.filters{display:flex;flex-wrap:wrap;gap:12px;align-items:flex-end;margin-bottom:16px;
  background:var(--surface);border:1px solid var(--border);border-radius:11px;padding:13px 14px;}
.fg{display:flex;flex-direction:column;gap:5px;}
.fg label{font-size:10px;color:var(--text2);letter-spacing:.08em;text-transform:uppercase;font-weight:600;}
.fg input,.fg select,.fg .date-input{background:var(--surface2);border:1px solid var(--border);
  color:var(--text);border-radius:7px;padding:8px 10px;font-family:inherit;font-size:12px;outline:none;}
.fg input:focus,.fg select:focus{border-color:var(--cyan);}
.fg .search{min-width:230px;}
.fg-reset{align-self:flex-end;}
.reset{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:7px;
  padding:9px 14px;font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;transition:filter .12s;}
.reset:hover{filter:brightness(1.2);}

/* tabs */
.tabs{display:flex;gap:8px;margin-bottom:14px;}
.tab{background:var(--surface2);color:var(--text2);border:1px solid var(--border);border-radius:8px;
  padding:9px 15px;font-weight:700;font-size:12px;cursor:pointer;font-family:inherit;letter-spacing:.03em;
  transition:all .12s;}
.tab:hover{color:var(--text);}
.tab.on{background:var(--surface);color:var(--cyan);box-shadow:inset 0 -2px 0 var(--cyan);border-color:var(--cyan);}

/* bulk bar */
.bulk{display:flex;align-items:center;justify-content:space-between;gap:12px;background:var(--surface);
  border:1px solid rgba(255,59,92,.4);border-radius:10px;padding:10px 14px;margin-bottom:12px;}
.bulk-info{font-size:12px;font-weight:700;color:var(--amber);}
.bulk-acts{display:flex;gap:9px;}
.bulk-acts button{background:var(--surface2);color:var(--text);border:1px solid var(--border);
  border-radius:7px;padding:8px 12px;font-weight:700;font-size:11.5px;cursor:pointer;font-family:inherit;
  transition:filter .12s;}
.bulk-acts button:hover{filter:brightness(1.2);}
.bulk-acts button.danger{background:var(--red);color:#fff;border-color:transparent;
  box-shadow:0 0 14px rgba(255,59,92,.3);}
.bulk-acts button:disabled{opacity:.5;cursor:not-allowed;}

/* table */
.tbl-wrap{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.tbl-hd{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:12px 15px;
  border-bottom:1px solid var(--border);flex-wrap:wrap;}
.tbl-hd .ti{font-size:11px;letter-spacing:.14em;text-transform:uppercase;color:var(--text2);font-weight:600;}
.pager{display:flex;align-items:center;gap:9px;font-size:11px;color:var(--text2);}
.pager .pinfo b{color:var(--text);}
.pager button{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;
  padding:6px 11px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;}
.pager button:hover:not(:disabled){filter:brightness(1.2);}
.pager button:disabled{opacity:.4;cursor:not-allowed;}
.pager select{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;
  padding:6px 9px;font-size:11px;font-family:inherit;}
.scroll{max-height:calc(100vh - 300px);min-height:200px;overflow:auto;}
table{width:100%;border-collapse:collapse;}
thead th{font-size:9.5px;letter-spacing:.1em;text-transform:uppercase;color:var(--text2);
  text-align:right;padding:9px 14px;font-weight:600;background:#0e1014;position:sticky;top:0;z-index:2;}
thead th:first-child,thead th.l{text-align:left;}
tbody td{padding:9px 14px;text-align:right;font-size:12.5px;border-top:1px solid #16191f;vertical-align:middle;}
tbody td:first-child,tbody td.l{text-align:left;}
tbody tr:hover{background:var(--surface2);}
.tk-name{font-weight:700;color:var(--text);cursor:pointer;}
.tk-name:hover{color:var(--cyan);}
.tk-addr{font-size:10px;color:var(--text2);}
.tk-sym{font-size:10px;color:var(--cyan);}
.pnl-pos{color:var(--green);font-weight:700;}.pnl-neg{color:var(--red);font-weight:700;}
.chain-badge{font-size:9.5px;font-weight:700;padding:2px 7px;border-radius:5px;letter-spacing:.04em;
  background:var(--surface2);border:1px solid var(--border);color:var(--text2);}
.tag{font-size:9.5px;font-weight:700;padding:2px 7px;border-radius:5px;letter-spacing:.04em;}
.tag.closed{background:rgba(0,224,138,.14);color:var(--green);}
.tag.partial{background:rgba(255,176,32,.16);color:var(--amber);}
.tag.open,.tag.active{background:rgba(34,211,238,.14);color:var(--cyan);}
.tag.other{background:rgba(138,143,152,.16);color:var(--text2);}
.tag.live{background:rgba(255,59,92,.16);color:var(--red);}
.tag.paper{background:rgba(0,224,138,.14);color:var(--green);}
.muted{color:var(--text2);}
.row-acts{display:flex;gap:7px;justify-content:flex-end;}
.btn-sm{background:var(--surface2);color:var(--text);border:1px solid var(--border);border-radius:6px;
  padding:6px 10px;font-size:11px;font-weight:700;cursor:pointer;font-family:inherit;transition:filter .12s;
  white-space:nowrap;}
.btn-sm:hover{filter:brightness(1.2);}
.btn-sm.close{background:var(--red);color:#fff;border-color:transparent;}
.btn-sm:disabled{opacity:.55;cursor:not-allowed;}
.no-data{padding:34px 14px;text-align:center;color:var(--text2);font-size:12.5px;
  border:1px dashed var(--border);border-radius:10px;}
.loading{padding:34px 14px;text-align:center;color:var(--text2);font-size:13px;}

/* stats grid */
.stats-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:11px;}
@media(max-width:980px){.stats-grid{grid-template-columns:repeat(2,1fr);}}
.scard{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:13px 14px;}
.scard .k{font-size:10px;color:var(--text2);letter-spacing:.07em;text-transform:uppercase;}
.scard .v{font-size:22px;font-weight:700;margin-top:5px;}
.scard .v.pos{color:var(--green);}.scard .v.neg{color:var(--red);}
.scard .sub{font-size:10.5px;color:var(--text2);margin-top:3px;}
.chain-blk{margin-top:18px;}
.chain-blk h3{font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:var(--text2);
  font-weight:600;margin-bottom:10px;}
.chain-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(130px,1fr));gap:10px;}
.chcard{background:var(--surface);border:1px solid var(--border);border-radius:9px;padding:10px 12px;}
.chcard .cn{font-size:11px;color:var(--text2);letter-spacing:.05em;}
.chcard .cp{font-size:16px;font-weight:700;margin-top:3px;}
.chcard .cp.pos{color:var(--green);}.chcard .cp.neg{color:var(--red);}
`;

const fmtSol = (v, d = 4) => (v === null || v === undefined || isNaN(v) ? "—" : Number(v).toFixed(d));

const formatDuration = (duration) => {
  if (!duration) return "—";
  const { days, hours, minutes, seconds } = duration;
  if (days > 0) return `${days}j ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds ?? 0}s`;
};

const formatPrice = (price) => {
  if (price === null || price === undefined) return "—";
  return `$${price}`;
};

const shortAddr = (a) => (a ? `${a.slice(0, 6)}...${a.slice(-4)}` : "—");

const statusTagClass = (status) => {
  if (status === "partial") return "partial";
  if (status === "closed") return "closed";
  if (status === "active" || status === "open") return "open";
  return "other";
};

// Badge de source d'achat (armé Wallet/Creator/Rule ou détection normale).
function SourceBadge({ source, activationId, onOpenDrill, onOpenRule }) {
  if (!source) return <span className="muted" style={{ fontSize: 10 }}>Direct</span>;
  const s = String(source).toLowerCase();
  // Extraire l'identifiant après le préfixe "wallet:"/"creator:"/"rule:"
  const addr = activationId ? activationId.replace(/^(wallet:|creator:|rule:)/i, "") : "";
  const shortA = addr ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : "";
  // Wallet/Creator => popup TokenDrillPanel ; Rule => popup RuleDrillPanel (addr = activation_id numérique).
  const drillKind = (s === "wallet" || s === "creator") && addr ? s : null;
  // addr peut être "genesis_rule:47" (arming P7.290) ou "47" : le drill/backtest attend
  // l'id entier de l'activation (discovery_rule_activations.id). Sinon → 422 int_parsing.
  const ruleId = (s === "rule" && addr) ? (String(addr).match(/(\d+)\s*$/)?.[1] || null) : null;
  const clickable = !!drillKind || !!ruleId;

  let icon, label, badgeStyle;
  if (s === "wallet") {
    icon = "👛"; label = `Wallet${shortA ? ` ${shortA}` : ''}`;
    badgeStyle = { background: "rgba(34,211,238,.15)", color: "var(--cyan)", border: "1px solid rgba(34,211,238,.3)" };
  } else if (s === "creator") {
    icon = "🏗"; label = `Creator${shortA ? ` ${shortA}` : ''}`;
    badgeStyle = { background: "rgba(255,176,32,.13)", color: "var(--amber)", border: "1px solid rgba(255,176,32,.25)" };
  } else if (s === "rule") {
    icon = "📏"; label = `Règle${addr ? ` #${addr}` : ''}`;
    badgeStyle = { background: "rgba(0,224,138,.12)", color: "var(--green)", border: "1px solid rgba(0,224,138,.25)" };
  } else {
    icon = ""; label = source;
    badgeStyle = { background: "var(--surface2)", color: "var(--text2)", border: "1px solid var(--border)" };
  }

  const onClick = clickable ? (e) => {
    e.stopPropagation();
    if (drillKind) onOpenDrill && onOpenDrill({ kind: drillKind, id: addr });
    else if (ruleId) onOpenRule && onOpenRule(ruleId);
  } : undefined;

  return (
    <span
      title={clickable ? `Voir le détail de ${activationId || addr}` : (activationId || source)}
      onClick={onClick}
      style={{
        ...badgeStyle,
        fontSize: 9.5, fontWeight: 700, padding: "2px 6px", borderRadius: 5,
        letterSpacing: ".03em", whiteSpace: "nowrap", cursor: clickable ? "pointer" : "default",
        display: "inline-block",
        textDecoration: clickable ? "underline dotted" : "none",
      }}
    >
      {icon} {label}
    </span>
  );
}

// P7.489 : mode propre à CHAQUE position (positions.mode), indépendant du badge global.
function PosModeTag({ mode }) {
  const m = (mode || "").toLowerCase();
  const cls = m === "live" ? "live" : m === "paper" ? "paper" : "other";
  return (
    <span className={`tag ${cls}`} style={{ marginLeft: 6 }} title="Mode de la position (positions.mode)">
      {m ? m.toUpperCase() : "?"}
    </span>
  );
}

// Décomposition d'entrée : % du prix au-dessus du launch à la DÉCISION d'achat (ratio→%).
// Loggé/persisté seulement pour les buys armés → null sinon (rien affiché).
function DecBadge({ value }) {
  if (value == null) return null;
  const pct = Math.round(value * 100);
  return (
    <div
      title="Prix à la décision d'achat vs launch (mesure 'on entre tard'). +308% = acheté quand le token avait déjà fait ×4 depuis le lancement."
      style={{ fontSize: 9, color: pct >= 100 ? "var(--red)" : "var(--text2)", marginTop: 2, whiteSpace: "nowrap" }}
    >
      entrée {pct >= 0 ? "+" : ""}{pct}% /launch
    </div>
  );
}

// Liens icônes GMGN + Dexscreener pour la cellule Token.
function TokenLinks({ tokenAddress }) {
  if (!tokenAddress) return null;
  return (
    <span style={{ marginLeft: 4 }}>
      <a href={`https://gmgn.ai/sol/token/${tokenAddress}`}
         target="_blank" rel="noopener noreferrer"
         onClick={(e) => e.stopPropagation()}
         title="Voir sur GMGN"
         style={{ color: "var(--cyan)", fontSize: 10, marginRight: 3, textDecoration: "none" }}>G</a>
      <a href={`https://dexscreener.com/solana/${tokenAddress}`}
         target="_blank" rel="noopener noreferrer"
         onClick={(e) => e.stopPropagation()}
         title="Voir sur Dexscreener"
         style={{ color: "var(--amber)", fontSize: 10, textDecoration: "none" }}>D</a>
    </span>
  );
}

// En-tête de colonne triable (tri serveur). Indicateur ▼/▲/↕.
function SortTh({ col, label, sort, onSort, className }) {
  const active = sort.by === col;
  return (
    <th
      className={className || ""}
      onClick={() => onSort(col)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", color: active ? "var(--cyan)" : undefined }}
      title="Trier (clic pour inverser le sens)"
    >
      {label}
      <span style={{ fontSize: 8, marginLeft: 3 }}>{active ? (sort.order === "desc" ? "▼" : "▲") : "↕"}</span>
    </th>
  );
}

export default function PositionsPage() {
  const [clock, setClock] = useState(new Date());
  const [activeTab, setActiveTab] = useState("active"); // 'active' | 'closed' | 'stats'

  // pagination — actives
  const [activePage, setActivePage] = useState(1);
  const [activeLimit, setActiveLimit] = useState(50);
  // pagination — fermées
  const [closedPage, setClosedPage] = useState(1);
  const [closedLimit, setClosedLimit] = useState(100);

  // filtres (persistés en sessionStorage — survivent à la navigation + refresh)
  const [searchTerm, setSearchTerm] = usePersistedState("positions.searchTerm", "");
  const [chainFilter, setChainFilter] = usePersistedState("positions.chainFilter", "");
  const [dateFrom, setDateFrom] = usePersistedState("positions.dateFrom", "");
  const [dateTo, setDateTo] = usePersistedState("positions.dateTo", "");
  // filtres serveur (query params — voir contrat positions_fixes_20260603)
  const [modeFilter, setModeFilter] = usePersistedState("positions.modeFilter", "all");       // all | live | paper
  const [shadowFilter, setShadowFilter] = usePersistedState("positions.shadowFilter", "exclude"); // exclude | only | all
  const [statusFilter, setStatusFilter] = usePersistedState("positions.statusFilter", "all");   // all | active | closed | partial | abandoned | migrated
  const [resultFilter, setResultFilter] = usePersistedState("positions.resultFilter", "all");   // all | win | loss
  const [sourceFilter, setSourceFilter] = usePersistedState("positions.sourceFilter", "all");   // all | <string>
  // Filtre multi-sélection SOURCE ARMÉE (axe différent de sourceFilter = source de détection).
  // Tableau vide = "Tous" (pas de filtre). Valeurs : wallet | creator | rule | direct.
  const [armedSourceFilter, setArmedSourceFilter] = usePersistedState("positions.armedSourceFilter", []);
  // Filtres outcomes (token_outcomes ⋈ bc_pda), pilotés par <OutcomesBar /> — style tokens.
  const [ocFilters, setOcFilters] = usePersistedState("positions.ocFilters", {
    min_peak: '', oc_migrated: '', oc_rugged: '', tp_min: '', tp_max: '', oc_life_min: '', oc_life_max: '',
  });

  // sélection / fermeture
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [drillSource, setDrillSource] = useState(null); // {kind:'wallet'|'creator', id} -> popup TokenDrillPanel
  const [drillRuleId, setDrillRuleId] = useState(null); // activation_id (string) -> popup RuleDrillPanel
  // Activations chargées paresseusement quand on clique sur un badge 📏 (résout id -> objet règle).
  const { data: ruleActsData } = useQuery({
    queryKey: ["positions-rule-activations"],
    queryFn: () => api.get("/discovery/rules/activations").then((r) => r.data),
    enabled: !!drillRuleId,
    staleTime: 30000,
  });
  const drillRule = drillRuleId
    ? (ruleActsData?.activations || []).find((a) => String(a.id) === String(drillRuleId)) || { id: Number(drillRuleId) }
    : null;
  const [selectedIds, setSelectedIds] = useState([]);
  const [closingIds, setClosingIds] = useState([]);

  // tri serveur par colonne (en-têtes cliquables) — actives & fermées indépendants
  const [activeSort, setActiveSort] = useState({ by: "ouvert", order: "desc" });
  const [closedSort, setClosedSort] = useState({ by: "ferme", order: "desc" });
  const sortActive = (by) => {
    setActiveSort((p) => ({ by, order: p.by === by && p.order === "desc" ? "asc" : "desc" }));
    setActivePage(1);
  };
  const sortClosed = (by) => {
    setClosedSort((p) => ({ by, order: p.by === by && p.order === "desc" ? "asc" : "desc" }));
    setClosedPage(1);
  };

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // ---- query params serveur (dates + filtres) — mêmes noms sur /active, /closed, /stats ----
  // Seuls les non-défauts sont envoyés → URLs propres, backend applique ses défauts (shadow=exclude).
  const filterParams = useMemo(() => {
    const params = new URLSearchParams();
    if (dateFrom) params.append("date_from", dateFrom);
    if (dateTo) params.append("date_to", dateTo);
    if (modeFilter !== "all") params.append("mode", modeFilter);
    if (shadowFilter !== "exclude") params.append("shadow", shadowFilter);
    if (statusFilter !== "all") params.append("status", statusFilter);
    if (resultFilter !== "all") params.append("result", resultFilter);
    if (sourceFilter !== "all") params.append("source", sourceFilter);
    // Source armée : multi-sélection, param répété (armed_source=wallet&armed_source=rule).
    // URLSearchParams.append plusieurs fois sur la même clé produit le format attendu par
    // FastAPI Query(None) -> List[str]. Tableau vide = pas de filtre (rétro-compat).
    (armedSourceFilter || []).forEach((v) => { if (v && v !== "all") params.append("armed_source", v); });
    // Filtres outcomes (OutcomesBar) : valeurs '' = absent → non envoyé. oc_migrated/oc_rugged
    // sont déjà ''/'true'/'false' (style tokens) → append direct, pas de mapping yes/no.
    ["min_peak", "oc_migrated", "oc_rugged", "tp_min", "tp_max", "oc_life_min", "oc_life_max"].forEach((k) => {
      if (ocFilters[k] !== "") params.append(k, ocFilters[k]);
    });
    return params.toString();
  }, [dateFrom, dateTo, modeFilter, shadowFilter, statusFilter, resultFilter, sourceFilter, armedSourceFilter, ocFilters]);
  const filterQuery = filterParams ? `&${filterParams}` : "";

  // ---- queries (polling react-query) ----
  const activeSkip = (activePage - 1) * activeLimit;
  const closedSkip = (closedPage - 1) * closedLimit;

  const activeSortQ = `&sort_by=${activeSort.by}&sort_order=${activeSort.order}`;
  const closedSortQ = `&sort_by=${closedSort.by}&sort_order=${closedSort.order}`;

  const activeQuery = useQuery({
    queryKey: ["positions", "active", activeSkip, activeLimit, filterParams, activeSort.by, activeSort.order],
    queryFn: async () =>
      (await api.get(`/positions/active?skip=${activeSkip}&limit=${activeLimit}${filterQuery}${activeSortQ}`)).data,
    refetchInterval: 5000,
    keepPreviousData: true,
  });
  const closedQuery = useQuery({
    queryKey: ["positions", "closed", closedSkip, closedLimit, filterParams, closedSort.by, closedSort.order],
    queryFn: async () =>
      (await api.get(`/positions/closed?skip=${closedSkip}&limit=${closedLimit}${filterQuery}${closedSortQ}`)).data,
    refetchInterval: 15000,
    keepPreviousData: true,
  });
  const statsQuery = useQuery({
    queryKey: ["positions", "stats", filterParams],
    queryFn: async () => (await api.get(`/positions/stats?${filterParams}`)).data,
    refetchInterval: 15000,
  });
  const riskQuery = useQuery({
    queryKey: ["positions", "risk"],
    queryFn: getRiskState,
    refetchInterval: 4000,
  });
  const walletQuery = useQuery({
    queryKey: ["positions", "wallet"],
    queryFn: getWalletBalance,
    refetchInterval: 10000,
  });

  const refetchAll = useCallback(() => {
    activeQuery.refetch();
    closedQuery.refetch();
    statsQuery.refetch();
  }, [activeQuery, closedQuery, statsQuery]);

  // ---- data extraction (shapes identiques à l'ancien composant) ----
  const activeData = activeQuery.data || {};
  const closedData = closedQuery.data || {};
  const stats = statsQuery.data || null;

  const activePositions = activeData.positions || [];
  const activeTotal =
    activeData.total_count ?? activeData.total ?? activePositions.length;
  const closedPositions = closedData.positions || [];
  const closedTotal = closedData.total ?? closedPositions.length;

  // P7.489 : mode du moteur (bot:mode > MODE env > unknown) — jamais PAPER par défaut.
  const modeView = runtimeModeView(riskQuery.data, { isError: riskQuery.isError });
  const w = walletQuery.data || {};

  const loading =
    (activeTab === "active" && activeQuery.isLoading) ||
    (activeTab === "closed" && closedQuery.isLoading);

  // ---- filtrage client (recherche + chaîne), identique à l'ancien ----
  const filterPositions = useCallback(
    (positions) =>
      positions.filter((pos) => {
        if (searchTerm) {
          const s = searchTerm.toLowerCase();
          const mAddr = (pos.token_address || "").toLowerCase().includes(s)
            || (pos.ui_mint || "").toLowerCase().includes(s);
          const mName = pos.token_name && pos.token_name.toLowerCase().includes(s);
          const mSym = pos.token_symbol && pos.token_symbol.toLowerCase().includes(s);
          if (!mAddr && !mName && !mSym) return false;
        }
        if (chainFilter && pos.chain !== chainFilter) return false;
        return true;
      }),
    [searchTerm, chainFilter]
  );

  const filteredActive = filterPositions(activePositions);
  const filteredClosed = filterPositions(closedPositions);

  // ---- KPI strip ----
  // Actifs DÉRIVÉS du tableau /active (poll 5s) → maj instantanée + cohérence avec les lignes.
  // Clôturées / PnL réalisé restent depuis /stats.
  const clp = stats?.closed_positions || {};
  const exposureUsd = useMemo(
    () => activePositions.reduce((s, p) => s + (Number(p.position_value) || 0), 0),
    [activePositions]
  );
  const unrealizedUsd = useMemo(
    () => activePositions.reduce((s, p) => s + (Number(p.pnl_usd) || 0), 0),
    [activePositions]
  );
  const availableSources = stats?.available_sources || [];

  // ---- actions close ----
  const isSelected = (id) => selectedIds.includes(id);
  const toggleSelect = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const selectAll = () => setSelectedIds(filteredActive.map((p) => p.id));
  const clearSelection = () => setSelectedIds([]);

  const handleCloseOne = async (id) => {
    if (!window.confirm("Confirmer la fermeture de cette position ?")) return;
    try {
      setClosingIds((prev) => [...prev, id]);
      await closePosition(id);
      setSelectedIds((prev) => prev.filter((x) => x !== id));
      await activeQuery.refetch();
      statsQuery.refetch();
    } catch (e) {
      console.error("Erreur fermeture position:", e);
      alert(e?.response?.data?.detail || "Erreur lors de la fermeture");
    } finally {
      setClosingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const handleCloseSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Fermer ${selectedIds.length} position(s) sélectionnée(s) ?`)) return;
    try {
      setClosingIds(selectedIds);
      const res = await closePositionsBatch(selectedIds, true);
      const { success_count, failure_count } = res || {};
      await activeQuery.refetch();
      statsQuery.refetch();
      clearSelection();
      alert(`Fermetures: ${success_count || 0} succès, ${failure_count || 0} échecs`);
    } catch (e) {
      console.error("Erreur fermeture batch:", e);
      alert(e?.response?.data?.detail || "Erreur lors de la fermeture multiple");
    } finally {
      setClosingIds([]);
    }
  };

  const resetFilters = () => {
    setSearchTerm("");
    setChainFilter("");
    setDateFrom("");
    setDateTo("");
    setModeFilter("all");
    setShadowFilter("exclude");
    setStatusFilter("all");
    setResultFilter("all");
    setSourceFilter("all");
    setArmedSourceFilter([]);
    setOcFilters({ min_peak: '', oc_migrated: '', oc_rugged: '', tp_min: '', tp_max: '', oc_life_min: '', oc_life_max: '' });
    setActivePage(1);
    setClosedPage(1);
  };

  // ---- toggle source armée (multi-sélection, tableau vide = "Tous") ----
  const toggleArmedSource = (val) => {
    setArmedSourceFilter((prev) =>
      prev.includes(val) ? prev.filter((x) => x !== val) : [...prev, val]
    );
    setActivePage(1);
    setClosedPage(1);
  };

  const t = clock.toLocaleTimeString("fr-FR", { hour12: false, timeZone: "Europe/Paris" });

  // ---- pagination calc ----
  const activeTotalPages = Math.max(1, Math.ceil((activeTotal || 0) / activeLimit));
  const closedTotalPages = Math.max(1, Math.ceil((closedTotal || 0) / closedLimit));

  return (
    <div className="ck">
      <style>{CSS}</style>

      <NavRail />

      <div className="main">
        <div className="top">
          <ModeBadge view={modeView} />

          <div className="wallet">
            <span className="lbl">Wallet</span>
            <span className="val">{w.configured === false ? "non config." : `${fmtSol(w.sol)} SOL`}</span>
          </div>

          {w.session_delta_sol != null && (
            <div className={`delta ${w.session_delta_sol < 0 ? "neg" : "pos"}`}>
              {w.session_delta_sol < 0 ? "▼" : "▲"}
              {`${w.session_delta_sol >= 0 ? "+" : ""}${fmtSol(w.session_delta_sol)} SOL`}
              <span style={{ color: "var(--text2)", fontWeight: 400, fontSize: 10 }}>session</span>
            </div>
          )}

          <div className="top-spacer" />
          <div className="clock"><b>{t}</b><br />Europe/Paris</div>
          <button
            className="refresh"
            onClick={refetchAll}
            disabled={activeQuery.isFetching || closedQuery.isFetching}
          >
            {activeQuery.isFetching || closedQuery.isFetching ? "⏳" : "🔄"} Actualiser
          </button>
        </div>

        <div className="page">
          {/* summary strip */}
          <div className="strip">
            <div className="kpi">
              <div className="k">Ouvertes</div>
              <div className="v">{activeTotal ?? 0}</div>
            </div>
            <div className="kpi">
              <div className="k">Exposition</div>
              <div className="v">{`$${exposureUsd.toFixed(2)}`}</div>
            </div>
            <div className="kpi">
              <div className="k">PnL latent</div>
              <div className={`v ${unrealizedUsd >= 0 ? "pos" : "neg"}`}>
                {`${unrealizedUsd >= 0 ? "+" : ""}$${unrealizedUsd.toFixed(2)}`}
              </div>
            </div>
            <div className="kpi">
              <div className="k">Clôturées</div>
              <div className="v">{(clp.count ?? closedTotal ?? 0).toLocaleString("fr-FR")}</div>
            </div>
            <div className="kpi">
              <div className="k">PnL réalisé</div>
              <div className={`v ${(clp.realized_pnl ?? 0) >= 0 ? "pos" : "neg"}`}>
                {clp.realized_pnl != null
                  ? `${clp.realized_pnl >= 0 ? "+" : ""}$${clp.realized_pnl.toFixed(0)}`
                  : "—"}
              </div>
            </div>
          </div>

          {/* filtres */}
          <div className="filters">
            <div className="fg">
              <label>🔍 Recherche</label>
              <input
                className="search"
                type="text"
                placeholder="Adresse, nom ou symbole..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="fg">
              <label>🔗 Chaîne</label>
              <select value={chainFilter} onChange={(e) => setChainFilter(e.target.value)}>
                <option value="">Toutes</option>
                <option value="solana">SOLANA</option>
                <option value="bsc">BSC</option>
                <option value="base">BASE</option>
                <option value="arbitrum">ARBITRUM</option>
                <option value="ethereum">ETHEREUM</option>
              </select>
            </div>
            <div className="fg">
              <label>⚙️ Mode</label>
              <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setActivePage(1); setClosedPage(1); }}>
                <option value="all">Tous</option>
                <option value="live">LIVE</option>
                <option value="paper">PAPER</option>
              </select>
            </div>
            <div className="fg">
              <label>👻 Shadow</label>
              <select value={shadowFilter} onChange={(e) => { setShadowFilter(e.target.value); setActivePage(1); setClosedPage(1); }}>
                <option value="exclude">Exclure</option>
                <option value="only">Seulement</option>
                <option value="all">Toutes</option>
              </select>
            </div>
            <div className="fg">
              <label>🏷️ Statut</label>
              <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setActivePage(1); setClosedPage(1); }}>
                <option value="all">Tous</option>
                <option value="active">Active</option>
                <option value="closed">Closed</option>
                <option value="partial">Partial</option>
                <option value="abandoned">Abandoned</option>
                <option value="migrated">Migrated</option>
              </select>
            </div>
            <div className="fg">
              <label>🎯 Résultat</label>
              <select value={resultFilter} onChange={(e) => { setResultFilter(e.target.value); setActivePage(1); setClosedPage(1); }}>
                <option value="all">Tous</option>
                <option value="win">Win</option>
                <option value="loss">Loss</option>
              </select>
            </div>
            <div className="fg">
              <label>📡 Source</label>
              <select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setActivePage(1); setClosedPage(1); }}>
                <option value="all">Toutes</option>
                {availableSources.map((src) => (
                  <option key={src} value={src}>{String(src).toUpperCase()}</option>
                ))}
              </select>
            </div>
            <div className="fg">
              <label>🎯 Source armée</label>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[
                  { val: "wallet", label: "👛 Wallet" },
                  { val: "creator", label: "🏗 Creator" },
                  { val: "rule", label: "📏 Rule" },
                  { val: "direct", label: "Direct" },
                ].map(({ val, label }) => {
                  const on = armedSourceFilter.includes(val);
                  return (
                    <button
                      key={val}
                      type="button"
                      onClick={() => toggleArmedSource(val)}
                      title={`Filtrer sur la source armée: ${label}`}
                      style={{
                        background: on ? "rgba(34,211,238,.15)" : "var(--surface2)",
                        color: on ? "var(--cyan)" : "var(--text)",
                        border: on ? "1px solid var(--cyan)" : "1px solid var(--border)",
                        borderRadius: 7, padding: "7px 10px", fontSize: 11, fontWeight: 700,
                        cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </button>
                  );
                })}
                {armedSourceFilter.length > 0 && (
                  <button
                    type="button"
                    onClick={() => { setArmedSourceFilter([]); setActivePage(1); setClosedPage(1); }}
                    title="Effacer le filtre source armée (Tous)"
                    style={{
                      background: "var(--surface2)", color: "var(--text2)", border: "1px solid var(--border)",
                      borderRadius: 7, padding: "7px 10px", fontSize: 11, fontWeight: 700,
                      cursor: "pointer", fontFamily: "inherit",
                    }}
                  >
                    Tous
                  </button>
                )}
              </div>
            </div>
            <div className="fg">
              <label>📅 Date de</label>
              <DateTimePicker value={dateFrom} onChange={(val) => { setDateFrom(val); setActivePage(1); setClosedPage(1); }} />
            </div>
            <div className="fg">
              <label>📅 Date à</label>
              <DateTimePicker value={dateTo} onChange={(val) => { setDateTo(val); setActivePage(1); setClosedPage(1); }} />
            </div>
            <div className="fg fg-reset">
              <button className="reset" onClick={resetFilters}>↻ Réinitialiser</button>
            </div>
          </div>

          {/* Filtres outcomes (PEAK · MIGRÉ · RUG · T-PEAK · SURVIE · TIME-TO-RUG) — même bloc que la page tokens */}
          <OutcomesBar
            filters={ocFilters}
            onChange={(patch) => { setOcFilters((p) => ({ ...p, ...patch })); setActivePage(1); setClosedPage(1); }}
          />

          {/* tabs */}
          <div className="tabs">
            <button className={`tab ${activeTab === "active" ? "on" : ""}`} onClick={() => setActiveTab("active")}>
              Actives ({activeTotal})
            </button>
            <button className={`tab ${activeTab === "closed" ? "on" : ""}`} onClick={() => setActiveTab("closed")}>
              Fermées ({closedTotal})
            </button>
            <button className={`tab ${activeTab === "stats" ? "on" : ""}`} onClick={() => setActiveTab("stats")}>
              Statistiques
            </button>
          </div>

          {/* ===== ACTIVE ===== */}
          {activeTab === "active" && (
            <>
              {selectedIds.length > 0 && (
                <div className="bulk">
                  <div className="bulk-info">{selectedIds.length} sélectionnée(s)</div>
                  <div className="bulk-acts">
                    <button onClick={selectAll}>Tout sélectionner</button>
                    <button onClick={clearSelection}>Effacer sélection</button>
                    <button className="danger" onClick={handleCloseSelected} disabled={closingIds.length > 0}>
                      🛑 Fermer sélection
                    </button>
                  </div>
                </div>
              )}
              <div className="tbl-wrap">
                <div className="tbl-hd">
                  <span className="ti">Positions ouvertes</span>
                  <div className="pager">
                    <span className="pinfo">{activeTotal} au total · page <b>{activePage}/{activeTotalPages}</b></span>
                    <button disabled={activePage <= 1} onClick={() => setActivePage((p) => Math.max(1, p - 1))}>← Préc.</button>
                    <button disabled={activePage >= activeTotalPages} onClick={() => setActivePage((p) => p + 1)}>Suiv. →</button>
                    <select value={activeLimit} onChange={(e) => { setActiveLimit(parseInt(e.target.value, 10)); setActivePage(1); }}>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={200}>200</option>
                    </select>
                  </div>
                </div>
                {loading ? (
                  <div className="loading">⏳ Chargement des positions...</div>
                ) : filteredActive.length === 0 ? (
                  <div className="no-data">
                    Aucune position active {searchTerm || chainFilter ? "correspondant aux filtres" : "pour le moment"}
                  </div>
                ) : (
                  <div className="scroll">
                    <table>
                      <thead>
                        <tr>
                          <th className="l">&nbsp;</th>
                          <SortTh col="token" label="Token" sort={activeSort} onSort={sortActive} className="l" />
                          <SortTh col="chain" label="Chain" sort={activeSort} onSort={sortActive} className="l" />
                          <th className="l">Source</th>
                          <SortTh col="investi" label="Investi" sort={activeSort} onSort={sortActive} />
                          <SortTh col="prix_entree" label="Prix entrée" sort={activeSort} onSort={sortActive} />
                          <SortTh col="pnl_pct" label="PnL %" sort={activeSort} onSort={sortActive} />
                          <SortTh col="pnl_usd" label="PnL USD" sort={activeSort} onSort={sortActive} />
                          <th>Valeur</th>
                          <SortTh col="duree" label="Durée" sort={activeSort} onSort={sortActive} />
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredActive.map((pos) => {
                          const pctPos = (pos.pnl_percent ?? 0) >= 0;
                          const usdPos = (pos.pnl_usd ?? 0) >= 0;
                          const invested = parseFloat(
                            pos.initial_amount ?? (pos.amount * pos.entry_price)
                          );
                          return (
                            <tr key={pos.id}>
                              <td className="l">
                                <input type="checkbox" checked={isSelected(pos.id)} onChange={() => toggleSelect(pos.id)} />
                              </td>
                              <td className="l">
                                {pos.token_name && (
                                  <div className="tk-name" onClick={() => setSelectedPosition(pos)} title={pos.token_name}>
                                    {pos.token_name}
                                  </div>
                                )}
                                <div className="tk-addr">
                                  {shortAddr(pos.ui_mint || pos.token_address)}
                                  <CopyBtn text={pos.ui_mint || pos.token_address} />
                                  <TokenLinks tokenAddress={pos.ui_mint || pos.token_address} />
                                </div>
                                {pos.token_symbol && <div className="tk-sym">{pos.token_symbol}</div>}
                              </td>
                              <td className="l">
                                <span className="chain-badge">{(pos.chain || "—").toUpperCase()}</span>
                                <PosModeTag mode={pos.mode} />
                                {pos.status === "partial" && (
                                  <span className="tag partial" style={{ marginLeft: 6 }} title="Position partiellement vendue">PARTIEL</span>
                                )}
                              </td>
                              <td className="l">
                                <SourceBadge source={pos.armed_source} activationId={pos.armed_activation_id} onOpenDrill={setDrillSource} onOpenRule={setDrillRuleId} />
                                <DecBadge value={pos.dec_pct_above_genesis} />
                              </td>
                              <td>SOL {isNaN(invested) ? "—" : invested.toFixed(4)}</td>
                              <td>{formatPrice(pos.entry_price)}</td>
                              <td className={pctPos ? "pnl-pos" : "pnl-neg"}>
                                {pctPos ? "+" : ""}{(pos.pnl_percent ?? 0).toFixed(2)}%
                              </td>
                              <td className={usdPos ? "pnl-pos" : "pnl-neg"}>
                                {usdPos ? "+" : ""}${(pos.pnl_usd ?? 0).toFixed(4)}
                              </td>
                              <td>{pos.position_value != null ? `$${pos.position_value.toFixed(2)}` : "—"}</td>
                              <td className="muted">{formatDuration(pos.holding_duration)}</td>
                              <td>
                                <div className="row-acts">
                                  <button className="btn-sm" onClick={() => setSelectedPosition(pos)} title="Détails et historique">
                                    📊 Détails
                                  </button>
                                  <button
                                    className="btn-sm close"
                                    onClick={() => handleCloseOne(pos.id)}
                                    disabled={closingIds.includes(pos.id)}
                                    title="Fermer la position"
                                  >
                                    {closingIds.includes(pos.id) ? "⏳" : "❌ Fermer"}
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ===== CLOSED ===== */}
          {activeTab === "closed" && (
            <div className="tbl-wrap">
              <div className="tbl-hd">
                <span className="ti">Trades clôturés</span>
                <div className="pager">
                  <span className="pinfo">{closedTotal} au total · page <b>{closedPage}/{closedTotalPages}</b></span>
                  <button disabled={closedPage <= 1} onClick={() => setClosedPage((p) => Math.max(1, p - 1))}>← Préc.</button>
                  <button disabled={closedPage >= closedTotalPages} onClick={() => setClosedPage((p) => p + 1)}>Suiv. →</button>
                  <select value={closedLimit} onChange={(e) => { setClosedLimit(parseInt(e.target.value, 10)); setClosedPage(1); }}>
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>
              </div>
              {loading ? (
                <div className="loading">⏳ Chargement des positions...</div>
              ) : filteredClosed.length === 0 ? (
                <div className="no-data">
                  Aucune position fermée {searchTerm || chainFilter ? "correspondant aux filtres" : "pour le moment"}
                </div>
              ) : (
                <div className="scroll">
                  <table>
                    <thead>
                      <tr>
                        <SortTh col="token" label="Token" sort={closedSort} onSort={sortClosed} className="l" />
                        <SortTh col="chain" label="Chain" sort={closedSort} onSort={sortClosed} className="l" />
                        <th className="l">Source</th>
                        <SortTh col="investi" label="Investi" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="prix_entree" label="Prix entrée" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="pnl_pct" label="PnL %" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="pnl_usd" label="PnL USD" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="peak" label="Peak%" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="duree" label="Durée" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="ouvert" label="Ouvert" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="ferme" label="Fermé" sort={closedSort} onSort={sortClosed} />
                        <SortTh col="status" label="Status" sort={closedSort} onSort={sortClosed} />
                        <th>Détails</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClosed.map((pos) => {
                        const pctPos = (pos.pnl_percent ?? 0) >= 0;
                        const usdPos = (pos.pnl_usd ?? 0) >= 0;
                        const invested = parseFloat(
                          pos.initial_amount ?? (pos.amount * pos.entry_price)
                        );
                        return (
                          <tr key={pos.id}>
                            <td className="l">
                              {pos.token_name && <div className="tk-name" onClick={() => setSelectedPosition(pos)}>{pos.token_name}</div>}
                              <div className="tk-addr">
                                {shortAddr(pos.ui_mint || pos.token_address)}
                                <CopyBtn text={pos.ui_mint || pos.token_address} />
                                <TokenLinks tokenAddress={pos.ui_mint || pos.token_address} />
                              </div>
                              {pos.token_symbol && <div className="tk-sym">{pos.token_symbol}</div>}
                            </td>
                            <td className="l"><span className="chain-badge">{(pos.chain || "—").toUpperCase()}</span><PosModeTag mode={pos.mode} /></td>
                            <td className="l">
                              <SourceBadge source={pos.armed_source} activationId={pos.armed_activation_id} onOpenDrill={setDrillSource} onOpenRule={setDrillRuleId} />
                                <DecBadge value={pos.dec_pct_above_genesis} />
                            </td>
                            <td>SOL {isNaN(invested) ? "—" : invested.toFixed(4)}</td>
                            <td>{formatPrice(pos.entry_price)}</td>
                            <td className={pctPos ? "pnl-pos" : "pnl-neg"} title={pos.pnl_source ? `source PnL : ${pos.pnl_source}` : undefined}>
                              {pctPos ? "+" : ""}{(pos.pnl_percent ?? 0).toFixed(2)}%
                            </td>
                            <td className={usdPos ? "pnl-pos" : "pnl-neg"}>
                              {usdPos ? "+" : ""}${(pos.pnl_usd ?? 0).toFixed(4)}
                            </td>
                            <td>
                              {/* G = peak du token vs GENESIS (ATH depuis le launch).
                                  E = peak vs NOTRE prix d'entrée. Dérivés de token_outcomes.peak_price
                                  (backend, base SOL/token). "bot" = exit_peak_pnl (peak observé par le
                                  bot pendant la détention seulement = clé de tri serveur). */}
                              {pos.peak_pct_genesis != null && (
                                <div className={pos.peak_pct_genesis >= 0 ? "pnl-pos" : "pnl-neg"}
                                  title="Peak du token vs son prix de genesis/launch (ATH depuis le lancement)">
                                  G {pos.peak_pct_genesis >= 0 ? "+" : ""}{pos.peak_pct_genesis.toFixed(1)}%
                                </div>
                              )}
                              {pos.peak_pct_entry != null && (
                                <div className={pos.peak_pct_entry >= 0 ? "pnl-pos" : "pnl-neg"}
                                  title="Peak du token vs NOTRE prix d'entrée (gain max théorique depuis l'achat)">
                                  E {pos.peak_pct_entry >= 0 ? "+" : ""}{pos.peak_pct_entry.toFixed(1)}%
                                </div>
                              )}
                              {pos.peak_pct_genesis == null && pos.peak_pct_entry == null && (
                                <span className="muted">—</span>
                              )}
                              {pos.exit_peak_pnl != null && (
                                <div className="muted" style={{ fontSize: 9 }}
                                  title="Peak PnL observé par le bot pendant la détention (fenêtre = durée du trade, souvent tronqué)">
                                  bot {pos.exit_peak_pnl >= 0 ? "+" : ""}{pos.exit_peak_pnl.toFixed(1)}%
                                </div>
                              )}
                              {pos.oc_migrated && <span style={{ marginLeft: 5, fontSize: 9, color: "var(--cyan)", fontWeight: 700 }}>migré</span>}
                            </td>
                            <td className="muted"
                              title={pos.duration_source === "closed_at" && pos.sell_tx_signature
                                ? "Durée = clôture en base, pas la vente (exit_duration_sec manquant)"
                                : undefined}>
                              {formatDuration(pos.holding_duration)}
                              {pos.duration_source === "closed_at" && pos.sell_tx_signature && (
                                <span style={{ marginLeft: 3, opacity: 0.6 }}>†</span>
                              )}
                            </td>
                            <td className="muted">{pos.opened_at ? new Date(pos.opened_at).toLocaleString("fr-FR") : "—"}</td>
                            <td className="muted">{pos.closed_at ? new Date(pos.closed_at).toLocaleString("fr-FR") : "—"}</td>
                            <td>
                              <span className={`tag ${statusTagClass(pos.status)}`}>
                                {(pos.status || "—").toString().toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div className="row-acts">
                                <button className="btn-sm" onClick={() => setSelectedPosition(pos)} title="Détails et historique">
                                  📊 Détails
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ===== STATS ===== */}
          {activeTab === "stats" && (
            <StatsTab stats={stats} />
          )}
        </div>
      </div>

      {/* modal détail (composant partagé inchangé) */}
      {drillSource && (
        <TokenDrillPanel kind={drillSource.kind} id={drillSource.id} onClose={() => setDrillSource(null)} />
      )}
      {drillRule && (
        <RuleDrillPanel rule={drillRule} onClose={() => setDrillRuleId(null)} />
      )}
      {selectedPosition && (
        <PositionDetailModal position={selectedPosition} onClose={() => setSelectedPosition(null)} />
      )}
    </div>
  );
}

function StatsTab({ stats }) {
  if (!stats) return <div className="loading">Chargement...</div>;

  const ap = stats.active_positions || { count: 0, total_value: 0, unrealized_pnl: 0 };
  const cp = stats.closed_positions || { realized_pnl: 0, win_rate: 0, winning_trades: 0, losing_trades: 0 };
  const ov = stats.overall || { best_trade: 0, avg_holding_hours: 0, total_pnl: 0, total_trades: 0 };
  const byChain = stats.pnl_by_chain || {};

  return (
    <div>
      <div className="stats-grid">
        <div className="scard">
          <div className="k">Positions actives</div>
          <div className="v">{ap.count || 0}</div>
          <div className="sub">Valeur: ${(ap.total_value || 0).toFixed(2)}</div>
        </div>
        <div className="scard">
          <div className="k">PnL non réalisé</div>
          <div className={`v ${(ap.unrealized_pnl || 0) >= 0 ? "pos" : "neg"}`}>
            {(ap.unrealized_pnl_percent || 0) >= 0 ? "+" : ""}{(ap.unrealized_pnl_percent || 0).toFixed(2)}%
          </div>
          <div className="sub">${(ap.unrealized_pnl || 0).toFixed(2)}</div>
        </div>
        <div className="scard">
          <div className="k">PnL réalisé</div>
          <div className={`v ${(cp.realized_pnl || 0) >= 0 ? "pos" : "neg"}`}>
            {(cp.realized_pnl_percent || 0) >= 0 ? "+" : ""}{(cp.realized_pnl_percent || 0).toFixed(2)}%
          </div>
          <div className="sub">${(cp.realized_pnl || 0).toFixed(2)}</div>
        </div>
        <div className="scard">
          <div className="k">Win rate</div>
          <div className="v">{(cp.win_rate || 0).toFixed(1)}%</div>
          <div className="sub">{cp.winning_trades || 0}W / {cp.losing_trades || 0}L</div>
        </div>
        <div className="scard">
          <div className="k">Best trade</div>
          <div className="v pos">${(ov.best_trade || 0).toFixed(2)}</div>
        </div>
        <div className="scard">
          <div className="k">Durée moy. holding</div>
          <div className="v">{(ov.avg_holding_hours || 0).toFixed(1)}h</div>
        </div>
        <div className="scard">
          <div className="k">PnL total</div>
          <div className={`v ${(ov.total_pnl || 0) >= 0 ? "pos" : "neg"}`}>
            {(ov.total_pnl_percent || 0) >= 0 ? "+" : ""}{(ov.total_pnl_percent || 0).toFixed(2)}%
          </div>
          <div className="sub">${(ov.total_pnl || 0).toFixed(2)}</div>
        </div>
        <div className="scard">
          <div className="k">Total trades</div>
          <div className="v">{ov.total_trades || 0}</div>
        </div>
      </div>

      {Object.keys(byChain).length > 0 && (
        <div className="chain-blk">
          <h3>PnL par blockchain</h3>
          <div className="chain-grid">
            {Object.entries(byChain).map(([chain, pnl]) => (
              <div key={chain} className="chcard">
                <div className="cn">{chain.toUpperCase()}</div>
                <div className={`cp ${pnl >= 0 ? "pos" : "neg"}`}>${Number(pnl).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
